/**
 * Ballot selection helpers for the proxy-voting page.
 *
 * Extracted so the active-ballot / nominee-count derivation used while
 * rendering a shareholder's ballot can be unit tested without pulling in
 * the rest of proxy-voting.js's DOM-coupled setup.
 */

/**
 * Derive the active ballot and its nominee/candidate-selection metadata.
 *
 * A campaign's `ballots` array is empty for an acknowledgement-only proxy
 * vote with no candidate slate (see admin/voting/voting.js buildVotingBallots,
 * which returns [] when there are zero candidates) — that's expected, not an
 * error condition. Guards `activeBallot` throughout so that case degrades to
 * "no nominees" instead of throwing on `undefined.candidateSelections` (#6083).
 *
 * @param {Array} ballots - activeCampaign.ballots
 * @param {boolean} twoDocs - whether this campaign renders two separate documents
 * @param {boolean} firstDocDone - whether the first document has been completed
 * @returns {{activeBallot: (Object|undefined), selectionCounts: (Array|null), numberOfNominees: number}}
 */
export function getBallotSelectionInfo(ballots, twoDocs, firstDocDone) {
	const activeBallot = twoDocs && firstDocDone ? ballots?.[1] : ballots?.[0];
	const selectionCounts = activeBallot?.candidateSelections ?? null;
	const nomineeCount = selectionCounts ? selectionCounts.findIndex((s) => s.length > 1) : -1;
	const numberOfNominees = nomineeCount === -1 ? (selectionCounts?.length ?? 0) : nomineeCount;
	return { activeBallot, selectionCounts, numberOfNominees };
}

/**
 * How many checkboxes each ballot-item type consumes when rendered/serialized.
 * Candidate slates keep today's for/against/abstain triplet; yes/no questions
 * are a mutually-exclusive pair; standalone checkboxes are a single toggle.
 */
const CHECKBOX_COUNT_BY_TYPE = {
	slate: 3,
	yesNo: 2,
	checkbox: 1,
};

/**
 * Build the `ballotItems` shape (see the voting-ballot-item-model plan's
 * Output Structure) from the three ordered label lists an admin enters in
 * the campaign-config chip UI.
 *
 * Mirrors `buildVotingBallots([])`'s existing behavior of producing nothing
 * for an acknowledgement-only campaign (#6083): when all three buckets are
 * empty, this returns `undefined` rather than an empty-bucketed object, so
 * `hasBallotItems()` correctly reports "legacy/no items" for that campaign.
 *
 * @param {Object} labels
 * @param {string[]} [labels.slates] - candidate-slate labels, in order
 * @param {string[]} [labels.yesNo] - yes/no question labels, in order
 * @param {string[]} [labels.checkboxes] - standalone checkbox labels, in order
 * @returns {({slates: {label: string}[], yesNo: {label: string}[], checkboxes: {label: string}[]}|undefined)}
 */
export function buildBallotItems({ slates = [], yesNo = [], checkboxes = [] } = {}) {
	if (!slates.length && !yesNo.length && !checkboxes.length) {
		return undefined;
	}
	return {
		slates: slates.map((label) => ({ label })),
		yesNo: yesNo.map((label) => ({ label })),
		checkboxes: checkboxes.map((label) => ({ label })),
	};
}

/**
 * Extract candidate names from a `ballotItems` shape, in order, for
 * populating the legacy `candidates` field (KTD2 — `candidates` stays
 * persisted alongside `ballotItems`, derived from the slate bucket).
 *
 * Returns `[]` when `ballotItems` is falsy (no items configured at all —
 * e.g. an acknowledgement-only campaign) so callers can always treat the
 * result as an array, matching `candidates`' existing always-an-array shape
 * elsewhere in the codebase.
 *
 * @param {({slates: {label: string}[]}|undefined|null)} ballotItems
 * @returns {string[]}
 */
export function deriveCandidateNames(ballotItems) {
	if (!ballotItems) {
		return [];
	}
	return (ballotItems.slates ?? []).map((s) => s.label);
}

/**
 * Total checkbox count a `ballotItems` shape expects to find in its
 * rendered ballot / uploaded template: 3 per candidate slate, 2 per yes/no
 * question, 1 per standalone checkbox (R5, R7).
 *
 * @param {({slates: Array, yesNo: Array, checkboxes: Array}|undefined|null)} ballotItems
 * @returns {number}
 */
export function expectedCheckboxCount(ballotItems) {
	if (!ballotItems) {
		return 0;
	}
	const { slates = [], yesNo = [], checkboxes = [] } = ballotItems;
	return slates.length * CHECKBOX_COUNT_BY_TYPE.slate + yesNo.length * CHECKBOX_COUNT_BY_TYPE.yesNo + checkboxes.length * CHECKBOX_COUNT_BY_TYPE.checkbox;
}

/**
 * Count unchecked checkbox glyphs (`&#9744;`) in a template's raw text
 * content, used to validate an uploaded template against
 * `expectedCheckboxCount()` (R7). Per this plan's Grounding, every template
 * that reaches production is already real HTML with literal entities even
 * when uploaded through the `.docx`-accepting dropzone, so no docx-XML
 * parsing is needed here.
 *
 * Deliberately does NOT count `&#9745;`/`&#9746;` (checked/crossed glyphs).
 * Those are not interchangeable with `&#9744;` in this template convention:
 * `proxy-voting.js`'s ballot-item render loop only ever consumes `&#9744;`
 * glyphs as real interactive checkboxes; `&#9745;`/`&#9746;` are separately
 * (and unconditionally) rewritten elsewhere in that file for unrelated
 * legacy features (a pre-checked display box, and a write-in-designee
 * checkbox) and can legitimately appear in a template as decorative content
 * that was never meant to become one of the configured ballot items -- e.g.
 * an instructional "mark your vote like this &#9746;" example glyph.
 * Counting them here previously produced a false mismatch warning on a
 * correctly-configured template that included such an example (confirmed
 * against USV's real "2026 Special Proxy Vote" template, which has exactly
 * one such instructional &#9746; alongside its 7 real &#9744; checkboxes).
 *
 * @param {string} text
 * @returns {number}
 */
export function countTemplateCheckboxGlyphs(text) {
	return (text.match(/&#9744;/g) || []).length;
}

/**
 * Walk a `ballotItems` shape in the fixed macro-order `slates → yesNo →
 * checkboxes` (KTD1), assigning each item the run of zero-based checkbox
 * indices it consumes (3 for a slate, 2 for a yesNo, 1 for a checkbox).
 *
 * This is the single shared definition of "checkbox index N belongs to
 * which item" that rendering/serialization (U3) and results-table parsing
 * (U4) both walk identically, so the walk order is defined exactly once
 * (KTD4). Returns `[]` for a falsy/empty `ballotItems`.
 *
 * @param {({slates: {label: string}[], yesNo: {label: string}[], checkboxes: {label: string}[]}|undefined|null)} ballotItems
 * @returns {Array<{item: {label: string}, type: ('slate'|'yesNo'|'checkbox'), checkboxIndices: number[]}>}
 *   One entry per ballot item, in walk order. `checkboxIndices` are the
 *   zero-based positions (into the flat, document-order list of rendered/
 *   serialized checkboxes) that this item owns.
 */
export function walkBallotItems(ballotItems) {
	if (!ballotItems) {
		return [];
	}
	const buckets = [
		{ type: 'slate', items: ballotItems.slates ?? [] },
		{ type: 'yesNo', items: ballotItems.yesNo ?? [] },
		{ type: 'checkbox', items: ballotItems.checkboxes ?? [] },
	];
	const entries = [];
	let cursor = 0;
	for (const bucket of buckets) {
		const checkboxCount = CHECKBOX_COUNT_BY_TYPE[bucket.type];
		for (const item of bucket.items) {
			const checkboxIndices = [];
			for (let i = 0; i < checkboxCount; i++) {
				checkboxIndices.push(cursor + i);
			}
			entries.push({ item, type: bucket.type, checkboxIndices });
			cursor += checkboxCount;
		}
	}
	return entries;
}

/**
 * Find the first ballot carrying a present `ballotItems` (KTD3) among a
 * campaign's ballots — the single shared lookup for "does this campaign
 * have a new-model ballot, and if so which one" that admin-config,
 * edit-form population, and results-table rendering all need identically.
 *
 * @param {(Array|undefined|null)} ballots
 * @returns {(Object|undefined)} the matching ballot, or `undefined` if none has `ballotItems`
 */
export function findBallotWithItems(ballots) {
	return ballots?.find((b) => hasBallotItems(b));
}

/**
 * Which ballot items need a mutually-exclusive checkbox group wired at
 * render/interaction time: candidate slates (for/against/abstain) and
 * yes/no questions (yes/no) are each a group where checking one box should
 * uncheck its sibling(s). Standalone checkboxes toggle independently and
 * are deliberately excluded from the result.
 *
 * This is the single source of truth for two things that must never drift
 * apart: how many exclusive-pair groups a ballot needs, and what each one
 * is named. It fixes a real bug in the pre-existing wiring loop, which was
 * bounded to `candidates.length + 1` groups -- correct only because there
 * was always exactly one hardcoded trailing yes/no group. A campaign with
 * two or more yes/no items needs one wired group per item, not one total;
 * both the rendering code (to name each group) and the exclusivity-wiring
 * loop (to know how many groups exist) call this function so they can't
 * disagree.
 *
 * @param {({slates: {label: string}[], yesNo: {label: string}[], checkboxes: {label: string}[]}|undefined|null)} ballotItems
 * @returns {Array<{item: {label: string}, type: ('slate'|'yesNo'), checkboxIndices: number[], groupName: string}>}
 *   One entry per slate/yesNo item, in walk order, each carrying the unique
 *   `groupName` ('group-1', 'group-2', ...) its checkboxes should share.
 */
export function getExclusiveGroupEntries(ballotItems) {
	return walkBallotItems(ballotItems)
		.filter((entry) => entry.type === 'slate' || entry.type === 'yesNo')
		.map((entry, index) => ({ ...entry, groupName: `group-${index + 1}` }));
}

/**
 * Build the vote-string digit-groups for a `ballotItems` shape from a flat
 * array of checkbox-checked booleans in document order -- the same order
 * `walkBallotItems()` assigns checkbox indices in. Returns the digit-groups
 * joined by `-`, matching today's separator convention (R5), generalized
 * from "always 3-wide" to whatever each item's own checkbox count declares.
 *
 * An item where no box is checked (e.g. a yes/no question the shareholder
 * left blank) emits an all-zero group (e.g. `00`) rather than throwing or
 * misaligning subsequent groups, because every item always contributes
 * exactly its declared digit count regardless of checked state.
 *
 * @param {({slates: Array, yesNo: Array, checkboxes: Array}|undefined|null)} ballotItems
 * @param {boolean[]} checkedStates - checked flag per flat checkbox index, in document order
 * @returns {string}
 */
export function buildVoteString(ballotItems, checkedStates) {
	return walkBallotItems(ballotItems)
		.map((entry) => entry.checkboxIndices.map((i) => (checkedStates[i] ? '1' : '0')).join(''))
		.join('-');
}

/**
 * Decode the vote-string digit-groups for a campaign's non-slate ballot
 * items (yes/no questions and standalone checkboxes) into a per-item answer
 * array for the admin results table (`user.itemAnswers`, KTD7).
 *
 * Takes the already-walked, non-slate entries (e.g. `walkBallotItems(ballotItems).filter(e
 * => e.type !== 'slate')` — the same computation the results table's `itemColumns` already
 * does once per render) rather than re-walking `ballotItems` itself. `accumulateVotes()`
 * calls this once per voter, so re-walking here would rebuild the same entries array on
 * every iteration for a `ballotItems` object that never changes across the loop.
 *
 * `votesArray` is the full vote string split on `-`, still including the
 * leading groups consumed by candidate slates -- `slateGroupCount` (the
 * unchanged `candidates.length` from `accumulateVotes()`, KTD2) says how
 * many of those leading groups to skip before mapping what's left onto
 * `nonSlateEntries`.
 *
 * Mirrors the results-table's existing attendance-decoding convention for
 * a two-digit group (`10` -> 'Yes', `01` -> 'No', anything else -- including
 * a shareholder leaving it blank as `00` -- -> '?', a neutral value rather
 * than a miscoded Yes/No). A standalone checkbox's single digit decodes as
 * checked/unchecked (`1` -> 'Yes', `0` -> 'No').
 *
 * @param {Array<{type: ('yesNo'|'checkbox')}>} nonSlateEntries - `walkBallotItems()` entries with slate items already filtered out, in walk order
 * @param {string[]} votesArray - the full vote string split on `-`
 * @param {number} slateGroupCount - number of leading groups to skip (`candidates.length`)
 * @returns {string[]} one decoded answer per entry in `nonSlateEntries`
 */
export function parseItemAnswers(nonSlateEntries, votesArray, slateGroupCount) {
	const remainingGroups = (votesArray || []).slice(slateGroupCount || 0);
	return (nonSlateEntries || []).map((entry, index) => {
		const group = remainingGroups[index];
		if (entry.type === 'checkbox') {
			return group === '1' ? 'Yes' : 'No';
		}
		if (group === '10') return 'Yes';
		if (group === '01') return 'No';
		return '?';
	});
}

/**
 * Backward-compatibility detection predicate (KTD3): a ballot is
 * "new-model" only when it carries a present `ballotItems`. Mirrors the
 * existing `selectionCounts` truthy/falsy branch pattern in
 * `getBallotSelectionInfo()` above. `buildBallotItems()` only ever produces
 * a present-and-non-empty `ballotItems` or `undefined`, so a simple
 * presence check is sufficient — no need to separately check bucket lengths.
 *
 * @param {(Object|undefined|null)} ballot
 * @returns {boolean}
 */
export function hasBallotItems(ballot) {
	return !!ballot?.ballotItems;
}

/**
 * Build the `userData` for one account in the ONE_FOR_ALL bulk-sign loop (a trustee
 * signing one ballot across every sub-addressed account they control).
 *
 * `acctPreviewUserData` (loadPreviewShareholder) is admin-only -- capTableData is
 * never populated for non-admin sessions, so it's null for a real trustee voting as
 * themselves. Falls back to `acct[7]` (the full record captured in
 * establishTrusteePreference()) rather than a `{shares, notes}` stand-in, so manual
 * cap-table custom columns (e.g. special_vote) don't resolve blank.
 *
 * @param {Array} acct - one entry from the `accounts` array (see establishTrusteePreference)
 * @param {Object|undefined|null} acctPreviewUserData - acctPreview?.userData from loadPreviewShareholder
 * @param {string} client - client slug
 * @returns {Object} userData shape for VariableProvider.buildCampaignVariables
 */
export function buildOneForAllAccountUserData(acct, acctPreviewUserData, client) {
	if (acctPreviewUserData) return acctPreviewUserData;
	return {
		user_name: acct[1],
		userid: acct[0],
		clients: { [client]: acct[7] || { shares: acct[5], notes: acct[4] } },
	};
}

/**
 * Filter the `accounts` array (see establishTrusteePreference) down to accounts that
 * still need a document for this campaign: owns shares in at least one campaign class,
 * and isn't yet signed for every class it owns. This is a per-campaign question, used
 * by the sequential one-at-a-time flow to build its queue -- distinct from the
 * ONE_FOR_ALL bulk loop's inline acctOwned/acctSigned check, which asks "is this account
 * done for the one class currently being rendered" and isn't interchangeable with this.
 *
 * Preserves the original `accounts` order.
 *
 * @param {Array} accounts - the `accounts` array from establishTrusteePreference()
 * @param {string[]} campaignClasses
 * @returns {Array} accounts still needing at least one document, in original order
 */
export function filterAccountsNeedingDocument(accounts, campaignClasses) {
	return accounts.filter((acct) => {
		const ownedByClass = acct[2] || {};
		const signedByClass = acct[3] || {};
		return campaignClasses.some((cls) => Number(ownedByClass[cls] || 0) > 0 && !signedByClass[cls]);
	});
}

/**
 * Count accounts (see establishTrusteePreference) that own shares in at least one campaign
 * class -- the stable total for the sequential flow's "Account N of M" denominator (U4).
 * Unlike filterAccountsNeedingDocument, this ignores signed status, so it doesn't shrink as
 * accounts complete. sequentialTotal must stay fixed for the whole flow: re-entering after
 * already signing 1 of 2 accounts in an earlier session should read "Account 2 of 2", not
 * "Account 1 of 1" (M shrinking to just what's still pending on this particular page load).
 *
 * @param {Array} accounts - the `accounts` array from establishTrusteePreference()
 * @param {string[]} campaignClasses
 * @returns {number} accounts owning shares in at least one campaign class
 */
export function countAccountsOwningShares(accounts, campaignClasses) {
	return accounts.filter((acct) => {
		const ownedByClass = acct[2] || {};
		return campaignClasses.some((cls) => Number(ownedByClass[cls] || 0) > 0);
	}).length;
}
